import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import AuroraNavbar from '../components/AuroraNavbar/AuroraNavbar';
import Footer from '../components/Footer';
import MetaHead from '../components/MetaHead';
import { API_BASE } from '../config/api';
import '../styles/pg-contact.css';

export default function Contact() {
  const { t, i18n } = useTranslation('contact');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState(''); // Anti-bot : un bot rempli tous les champs visibles
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError(t('errors.all_required'));
      return;
    }
    if (!email.includes('@')) {
      setError(t('errors.email_invalid'));
      return;
    }

    setStatus('sending');

    try {
      const res = await fetch(`${API_BASE}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // lang : transmise pour que le backend renvoie une confirmation
        // dans la bonne langue (whitelist serveur sur fr|en|es|it, fallback fr).
        body: JSON.stringify({ name, email, message, company: honeypot, lang: i18n.language })
      });

      let data = {};
      try { data = await res.json(); } catch (_) {}

      if (!res.ok) {
        console.error('Contact: HTTP non-OK', { status: res.status, data });
        throw new Error(data.error || t('errors.send_error'));
      }

      setStatus('sent');
      setName(''); setEmail(''); setMessage('');
    } catch (err) {
      console.error('Contact: exception', err);
      setError(err.message || t('errors.send_error_short'));
      setStatus('idle');
    }
  };

  return (
    <>
      <MetaHead page="contact" />
      <AuroraBackground />
      <AuroraNavbar />

      <main className="pg-contact">
        <div className="pg-contact-inner">
          <h1 className="pg-contact-heading">{t('title')}</h1>
          <p className="pg-contact-subheading">
            {t('subtitle_part1')}{' '}
            {t('subtitle_part2')}{' '}
            <a href="mailto:contact@getrevyo.com">contact@getrevyo.com</a>.
          </p>

          {status === 'sent' ? (
            <div className="pg-contact-success-card">
              <div className="pg-contact-success-icon">✓</div>
              <h2 className="pg-contact-success-heading">{t('success_heading')}</h2>
              <p className="pg-contact-success-body">{t('success')}</p>
              <Link to="/" className="pg-contact-success-back">{t('success_back')}</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="pg-contact-card" noValidate>
              {/* Honeypot : invisible aux humains, rempli par les bots → refus côté backend */}
              <div className="pg-contact-honeypot" aria-hidden="true">
                <label htmlFor="company">{t('form.honeypot_label')}</label>
                <input
                  id="company"
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              <div className="pg-contact-field">
                <label className="pg-contact-label" htmlFor="contact-name">{t('form.name_label')}</label>
                <input
                  id="contact-name"
                  type="text"
                  className="pg-contact-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('form.name_placeholder')}
                  required
                />
              </div>

              <div className="pg-contact-field">
                <label className="pg-contact-label" htmlFor="contact-email">{t('form.email_label')}</label>
                <input
                  id="contact-email"
                  type="email"
                  className="pg-contact-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('form.email_placeholder')}
                  required
                />
              </div>

              <div className="pg-contact-field">
                <label className="pg-contact-label" htmlFor="contact-message">{t('form.message_label')}</label>
                <textarea
                  id="contact-message"
                  className="pg-contact-textarea"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('form.message_placeholder')}
                  required
                />
              </div>

              <button
                type="submit"
                className="pg-contact-btn"
                disabled={status === 'sending' || status === 'sent'}
              >
                {status === 'sending' ? t('form.submit_sending') : t('form.submit_idle')}
              </button>

              {error && <div className="pg-contact-error">{error}</div>}
            </form>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
